import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Search, X } from "lucide-react";
import CategoryHero from "./CategoryHero";
import NewsTicker from "./NewsTicker";
import { EVENTS, EventCard, isEventUpcoming, isEventInMonth, parseLocal, type EventItem } from "./Events";
import { RestaurantCard, useAllRestaurantPlaces } from "./Restaurants";
import { FEATURED_BUSINESS_DEALS, FeaturedDealCard } from "./Deals";
import { CATERERS, CatererCard, isFeaturedCaterer, type Caterer } from "./Catering";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import type { PlaceResult } from "../hooks/useGooglePlaces";

const TODAY = new Date();
const CURRENT_MONTH_NAME = TODAY.toLocaleString("en-US", { month: "long" });

// Current-month events pulled live from the Concerts/Events page — reads the SAME
// managed collection ("events_featured") the Events page uses, so concerts the
// owner adds via Admin show up here automatically, not just the curated code
// defaults. Recomputed off today's date, so this rolls forward every month with
// no manual editing.
function useCurrentMonthEvents(): EventItem[] {
  const mc = useManagedCollection<any>("events_featured", EVENTS, (e) => slugify(e.id || e.name));
  return useMemo(
    () =>
      (mc.items as EventItem[])
        .filter((e) => isEventInMonth(e, TODAY.getFullYear(), TODAY.getMonth()))
        .sort((a, b) => (a.date || "").localeCompare(b.date || "")),
    [mc.items]
  );
}

// Chaitanya's Delights (the TastebudsByAnubhi-featured caterer) replicated from
// the Catering page into the New Openings section. Reads the SAME managed
// "catering" collection the Catering page uses, so the copy stays in sync with
// dashboard edits automatically — no duplicated hardcoded data.
function useFeaturedCaterer(): Caterer | undefined {
  const mc = useManagedCollection<any>("catering", CATERERS, (c, i) => slugify(c.name || String(i)));
  return useMemo(
    () => (mc.items as Caterer[]).find((c) => isFeaturedCaterer(c)),
    [mc.items]
  );
}

// Haldirams (7010 Preston Rd ste 240, Frisco) — the TastebudsByAnubhi-featured
// restaurant replicated from the Restaurants page into the New Openings
// section. Reads the SAME live merge (useAllRestaurantPlaces) the Restaurants
// page uses, so the copy stays in sync with dashboard edits automatically and
// keeps its "Featured by TastebudsByAnubhi" badge + Instagram link.
function useFeaturedHaldirams(): PlaceResult | undefined {
  const { items } = useAllRestaurantPlaces();
  return useMemo(
    () =>
      items.find(
        (r) =>
          (r.name || "").toLowerCase().includes("haldirams") &&
          (r.address || "").toLowerCase().includes("7010 preston")
      ),
    [items]
  );
}

// New Openings — exact replicas of specific cards from the Restaurants page
// (same name, address, image, cuisines). Display-only copies; the Restaurants
// page's own managed collections are untouched.
const NEW_OPENING_RESTAURANTS: PlaceResult[] = [
  {
    id: "opening-the-pulao-company",
    name: "The Pulao Company",
    location: "Frisco",
    city: "Frisco",
    address: "13089 Main St #400, Frisco, TX 75035",
    rating: 0,
    reviewCount: 0,
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783365332940-h8p7de.png",
    placeId: "opening-the-pulao-company",
    website: "",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent("The Pulao Company, 13089 Main St #400, Frisco, TX 75035"),
    cuisines: ["South Indian"],
  },
  {
    id: "opening-hyderabad-wala",
    name: "Hyderabad Wala",
    location: "McKinney",
    city: "McKinney",
    address: "2414 W University Dr Ste. 200, McKinney, TX 75071",
    rating: 0,
    reviewCount: 0,
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-06/67cc0c3672fb.jpeg",
    placeId: "opening-hyderabad-wala",
    website: "",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent("Hyderabad Wala, 2414 W University Dr Ste. 200, McKinney, TX 75071"),
    cuisines: ["South Indian"],
  },
  {
    id: "opening-garam-masala-kitchen",
    name: "Garam Masala Kitchen",
    location: "Melissa",
    city: "Melissa",
    address: "3310 Sam Rayburn Hwy Ste 500, Melissa, TX 75454",
    rating: 4.0,
    reviewCount: 0,
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783365106776-esph7.png",
    placeId: "static-garam-masala-kitchen",
    website: "",
    mapsUrl: "https://www.google.com/maps/search/Garam+Masala+Kitchen+3310+Sam+Rayburn+Hwy+Ste+500+Melissa+TX+75454",
    cuisines: ["Indian"],
  },
  {
    id: "opening-gt-road",
    name: "GT Road",
    location: "Little Elm",
    city: "Little Elm",
    address: "11700 FM 423 Unit 140, Little Elm, TX 75068",
    rating: 0,
    reviewCount: 0,
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1786992747509-m8ubvr.png",
    placeId: "opening-gt-road",
    website: "",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent("GT Road, 11700 FM 423 Unit 140, Little Elm, TX 75068"),
    cuisines: ["Pakistani", "Halal"],
  },
  {
    id: "opening-birista-house",
    name: "Birista House by PRISM BAR n GRILL",
    location: "Frisco",
    city: "Frisco",
    address: "6377 Custer Rd, Frisco, TX 75035",
    rating: 0,
    reviewCount: 0,
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1787952050925-p4u35e.jpg",
    placeId: "opening-birista-house",
    website: "",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent("Birista House by PRISM BAR n GRILL, 6377 Custer Rd, Frisco, TX 75035"),
    cuisines: ["Bar & Grill"],
    instagram: "https://www.instagram.com/reel/DceHfRqxK07/?utm_source=ig_web_copy_link&igsi=MzRlODBiNWFlZA==",
  },
  {
    id: "opening-hashtag-india-lewisville",
    name: "Hashtag India Lewisville",
    location: "Lewisville",
    city: "Lewisville",
    address: "4440 State Hwy 121 Ste 5, Lewisville, TX 75056",
    rating: 0,
    reviewCount: 0,
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1788361344117-iubxdw.jpg",
    placeId: "opening-hashtag-india-lewisville",
    website: "",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent("Hashtag India Lewisville, 4440 State Hwy 121 Ste 5, Lewisville, TX 75056"),
    cuisines: ["South Indian", "Street Food"],
    instagram: "https://www.instagram.com/reel/Dcw2cZWM-a-/",
  },
  {
    id: "opening-lokal-eatz-mckinney",
    name: "LoKal Eatz McKinney",
    location: "McKinney",
    city: "McKinney",
    address: "6631 Co Rd 124, McKinney, TX 75071",
    rating: 0,
    reviewCount: 0,
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1791398038323-1tvp0g.jpg",
    placeId: "opening-lokal-eatz-mckinney",
    website: "",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent("LoKal Eatz McKinney, 6631 Co Rd 124, McKinney, TX 75071"),
    cuisines: ["Street Food"],
    instagram: "https://www.instagram.com/p/DdwvtrQs78z/",
  },
];

const TABS = [
  { id: "all", label: "All" },
  { id: "events", label: "Events Scoop" },
  { id: "deals", label: "Deals Scoop" },
  { id: "openings", label: "New Openings" },
  { id: "celebrity", label: "Celebrity Scoop" },
] as const;

type TabId = (typeof TABS)[number]["id"];

export default function DallasWeeklyScoop() {
  const [activeTab, setActiveTab] = useState<TabId>("all");
  const [scoopQuery, setScoopQuery] = useState("");

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Dallas Monthly Scoop"
        placeholder="Search the scoop…"
      />
      <NewsTicker />

      <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 py-8">
        {/* Tab bar + search */}
        <div className="border-b border-gray-200 mb-8">
          <div className="flex items-center gap-4 overflow-x-auto scrollbar-none">
            <div className="flex justify-start space-x-6 md:space-x-10 min-w-max px-2">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="relative pb-3 pt-1 px-1 flex items-center text-sm md:text-base font-semibold transition-all duration-200 focus:outline-none group whitespace-nowrap"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      color: isActive ? "#082f6d" : "#6b7280",
                    }}
                  >
                    <Flame
                      className={`w-4 h-4 mr-1.5 transition-all duration-300 ${
                        isActive 
                          ? "text-orange-500 scale-110 animate-pulse" 
                          : "text-gray-400 group-hover:text-orange-500 group-hover:scale-110"
                      }`}
                    />
                    <span className={`transition-colors duration-200 ${isActive ? "text-[#082f6d]" : "group-hover:text-[#082f6d]"}`}>
                      {tab.label}
                    </span>

                    {/* Active underline indicator */}
                    {isActive ? (
                      <motion.div
                        layoutId="activeTabUnderline"
                        className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#082f6d]"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    ) : (
                      /* Hover subtle preview underline */
                      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-transparent group-hover:bg-[#082f6d]/20 transition-all duration-200" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Search bar — sits right next to the tabs (New Openings), not pushed to the far end */}
            <div className="relative flex-shrink-0 mb-3 w-36 sm:w-48 md:w-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={scoopQuery}
                onChange={(e) => setScoopQuery(e.target.value)}
                placeholder="Search…"
                aria-label="Search the Dallas Monthly Scoop"
                className="w-full pl-9 pr-8 py-2 text-sm rounded-full border border-gray-200 outline-none focus:border-[#082f6d] transition-colors duration-200"
                style={{ fontFamily: "'Montserrat', sans-serif", color: "#1f2937" }}
              />
              {scoopQuery && (
                <button
                  type="button"
                  onClick={() => setScoopQuery("")}
                  aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tab content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab + scoopQuery}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
          >
            {activeTab === "all" && <AllScoopTab query={scoopQuery} />}
            {activeTab === "events" && <EventsScoopTab query={scoopQuery} />}
            {activeTab === "deals" && <DealsScoopTab query={scoopQuery} />}
            {activeTab === "openings" && <NewOpeningsTab query={scoopQuery} />}
            {activeTab === "celebrity" && <CelebrityScoopTab query={scoopQuery} />}
          </motion.div>
        </AnimatePresence>

        {/* Newsletter CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-12 text-center p-8 rounded-2xl"
          style={{
            background: "linear-gradient(135deg, #082f6d 0%, #0a3d8a 100%)",
          }}
        >
          <h3
            className="text-2xl font-bold text-white"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Never Miss the Scoop
          </h3>
          <p
            className="mt-2 text-white/80"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Get the hottest Dallas updates delivered straight to your inbox every month.
          </p>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              placeholder="Your email address"
              className="w-full sm:flex-1 px-4 py-3 rounded-lg text-sm outline-none"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            />
            <button
              type="submit"
              className="px-6 py-3 rounded-lg font-bold text-sm transition-colors duration-200 whitespace-nowrap"
              style={{
                backgroundColor: "#f59e0b",
                color: "#082f6d",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              Subscribe
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}

/* ──────────────── Tab Content Components ──────────────── */

function matchesQuery(q: string, ...fields: (string | undefined)[]) {
  if (!q.trim()) return true;
  const needle = q.trim().toLowerCase();
  return fields.some((f) => f && f.toLowerCase().includes(needle));
}

// Extracts the first "H:MM AM/PM" (or "H AM/PM") occurrence from a free-form time
// string like "11:00 AM - 7:00 PM" or "Doors Open 5:30 PM" and converts it to
// minutes-since-midnight for sorting. Events with no parseable time sort after
// timed events that share the same date.
function parseStartMinutes(time?: string): number {
  if (!time) return 24 * 60;
  const match = time.match(/(\d{1,2})(?::(\d{2}))?\s*([AaPp][Mm])/);
  if (!match) return 24 * 60;
  let hours = parseInt(match[1], 10) % 12;
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  if (/[Pp][Mm]/.test(match[3])) hours += 12;
  return hours * 60 + minutes;
}

// Upcoming events only (past events excluded), nearest date first; ties on the
// same date are broken by start time, earliest first.
function sortUpcomingEvents(events: EventItem[]): EventItem[] {
  return events
    .filter(isEventUpcoming)
    .slice()
    .sort((a, b) => {
      const dateDiff = parseLocal(a.date || "").getTime() - parseLocal(b.date || "").getTime();
      if (dateDiff !== 0) return dateDiff;
      return parseStartMinutes(a.time) - parseStartMinutes(b.time);
    });
}

function EmptyResults({ label }: { label: string }) {
  return (
    <p className="text-sm text-gray-400 py-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      No {label} match your search.
    </p>
  );
}

const NEW_OPENINGS_INTRO_PARAGRAPHS = [
  "Discover the latest restaurant openings, new Indian restaurants, and exciting dining spots across the Dallas–Fort Worth area with DallasDesiScoop. Stay updated on the newest places to eat, from Indian, Pakistani, South Asian, Mediterranean, Middle Eastern, and other popular cuisines.",
  "Explore new restaurant openings in Dallas, Frisco, Plano, Irving, McKinney, Allen, The Colony, Prosper, Lewisville, Carrollton, Coppell, Richardson, Flower Mound, Little Elm, and surrounding DFW communities.",
  "Find new restaurants near you, grand openings, newly opened Indian restaurants, new Desi restaurants, and exciting food spots joining the DFW dining scene.",
  "Whether you're looking for the newest restaurants in Dallas, new Indian restaurants in Frisco or Plano, or the latest restaurant openings across DFW, DallasDesiScoop keeps you in the know about where to eat next.",
];

function NewOpeningsIntro() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [firstParagraph, ...restParagraphs] = NEW_OPENINGS_INTRO_PARAGRAPHS;
  return (
    <>
      {/* Mobile: heading + first paragraph + Read more toggle */}
      <div className="sm:hidden">
        <h2
          className="text-[17px] font-bold mb-2"
          style={{ fontFamily: "'Poppins', sans-serif", color: "#082f6d" }}
        >
          New Restaurant Openings in Dallas–Fort Worth
        </h2>
        <p className="text-[12px] text-gray-500 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          {firstParagraph}
        </p>
        {isExpanded && restParagraphs.map((para, i) => (
          <p key={i} className="text-[12px] text-gray-500 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {para}
          </p>
        ))}
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="text-[12px] font-semibold mb-4"
          style={{ fontFamily: "'Montserrat', sans-serif", color: "#082f6d" }}
        >
          {isExpanded ? "Read less" : "Read more"}
        </button>
      </div>

      {/* Desktop / tablet: full text, no truncation */}
      <div className="hidden sm:block">
        <h2
          className="text-xl md:text-2xl font-bold mb-3"
          style={{ fontFamily: "'Poppins', sans-serif", color: "#082f6d" }}
        >
          New Restaurant Openings in Dallas–Fort Worth
        </h2>
        {NEW_OPENINGS_INTRO_PARAGRAPHS.map((para, i) => (
          <p
            key={i}
            className={`text-gray-600 max-w-3xl ${i === NEW_OPENINGS_INTRO_PARAGRAPHS.length - 1 ? "mb-6" : "mb-3"}`}
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {para}
          </p>
        ))}
      </div>
    </>
  );
}

function AllScoopTab({ query = "" }: { query?: string }) {
  const currentMonthEvents = useCurrentMonthEvents();
  const featuredCaterer = useFeaturedCaterer();
  const showFeaturedCaterer =
    !!featuredCaterer &&
    matchesQuery(query, featuredCaterer.name, featuredCaterer.address, featuredCaterer.location);
  const featuredHaldirams = useFeaturedHaldirams();
  const showFeaturedHaldirams =
    !!featuredHaldirams &&
    matchesQuery(query, featuredHaldirams.name, featuredHaldirams.address, featuredHaldirams.city);
  const events = sortUpcomingEvents(
    currentMonthEvents.filter((e) => matchesQuery(query, e.name, e.venue, e.city, e.category))
  );
  const deals = FEATURED_BUSINESS_DEALS.filter((d) => matchesQuery(query, d.businessName, d.dealTitle, d.description, d.category));
  const openings = NEW_OPENING_RESTAURANTS.filter((r) => matchesQuery(query, r.name, r.address, r.city, ...(r.cuisines || [])));
  const hasAny = events.length || deals.length || openings.length || showFeaturedCaterer || showFeaturedHaldirams;

  if (query.trim() && !hasAny) {
    return <EmptyResults label="results" />;
  }

  return (
    <div className="space-y-10">
      {/* Events section */}
      {(!query.trim() || events.length > 0) && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Flame className="w-5 h-5 text-orange-500" />
            <h2
              className="text-lg font-bold"
              style={{ fontFamily: "'Poppins', sans-serif", color: "#082f6d" }}
            >
              Events Scoop
            </h2>
          </div>
          <p
            className="text-gray-600 mb-6 max-w-3xl"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Discover the latest Desi events and concerts in Dallas-Fort Worth, including Indian and Pakistani events, Bollywood concerts, cultural festivals, dance shows, comedy, food events, community gatherings, Diwali, Holi, Navratri, Dandiya, Garba, Eid celebrations, and more. Find upcoming Dallas events, Frisco events, Plano events, and DFW Desi events all in one place.
          </p>
          {events.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {events.map((event, i) => (
                <EventCard key={(event as any)._key || event.id} event={event} index={i} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              No {CURRENT_MONTH_NAME} events yet — check back soon.
            </p>
          )}
        </div>
      )}

      {/* Deals section */}
      {(!query.trim() || deals.length > 0) && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Flame className="w-5 h-5 text-blue-500" />
            <h2
              className="text-lg font-bold"
              style={{ fontFamily: "'Poppins', sans-serif", color: "#082f6d" }}
            >
              Deals Scoop
            </h2>
          </div>
          <p
            className="text-sm text-gray-600 mb-4 max-w-3xl"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Discover the latest restaurant deals in Dallas–Fort Worth, including Indian, Pakistani, Mediterranean, Desi, and local restaurant specials, discounts, promotions, happy hours, buffet deals, lunch specials, BOGO offers, and limited-time food deals. Find the best Dallas restaurant deals, Frisco food deals, Plano restaurant specials, and DFW dining offers all in one place.
          </p>
          <DealsScoopImageStrip />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {deals.map((deal, i) => (
              <FeaturedDealCard key={deal._id} deal={deal} index={i} imageClassName="w-full h-[160px] object-cover" />
            ))}
          </div>
        </div>
      )}

      {/* New Openings section */}
      {(!query.trim() || openings.length > 0 || showFeaturedCaterer || showFeaturedHaldirams) && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Flame className="w-5 h-5 text-red-500" />
            <h2
              className="text-lg font-bold"
              style={{ fontFamily: "'Poppins', sans-serif", color: "#082f6d" }}
            >
              New Openings
            </h2>
          </div>
          <NewOpeningsIntro />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {openings.map((r, i) => (
              <RestaurantCard key={r.id} restaurant={r} index={i} />
            ))}
            {showFeaturedCaterer && featuredCaterer && (
              <CatererCard
                caterer={featuredCaterer}
                index={openings.length}
                isFeatured
              />
            )}
            {showFeaturedHaldirams && featuredHaldirams && (
              <RestaurantCard
                key={featuredHaldirams.id || "featured-haldirams"}
                restaurant={featuredHaldirams}
                index={openings.length + (showFeaturedCaterer ? 1 : 0)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EventsScoopTab({ query = "" }: { query?: string }) {
  const currentMonthEvents = useCurrentMonthEvents();
  const events = sortUpcomingEvents(
    currentMonthEvents.filter((e) => matchesQuery(query, e.name, e.venue, e.city, e.category))
  );
  if (events.length === 0) {
    return query.trim() ? (
      <EmptyResults label="events" />
    ) : (
      <p className="text-sm text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>
        No {CURRENT_MONTH_NAME} events yet — check back soon.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {events.map((event, i) => (
        <EventCard key={(event as any)._key || event.id} event={event} index={i} />
      ))}
    </div>
  );
}

// Deal promo images uploaded by the owner — shown at the top of the Deals Scoop
// section (both the All tab and the Deals Scoop tab).
const DEALS_SCOOP_IMAGES = [
  "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1790136568333-rkq7ys.jpg",
  "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1790136568046-isvr.jpg",
  "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1790136567855-zhzpfy.jpg",
  "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1790136568132-41llpm.jpg",
  "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1790136568100-1mrdvl.jpg",
  "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1790136772647-qsljst.jpg",
  "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1790136772355-25ikyn.jpg",
  "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1790770024363-h3bxz.jpg",
];

// Promo tiles the owner has marked as closed — shown with a red "Closed" badge.
const DEALS_SCOOP_CLOSED = new Set([
  "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1790770024363-h3bxz.jpg",
]);

function DealsScoopImageStrip() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
      {DEALS_SCOOP_IMAGES.map((src, i) => {
        const isClosed = DEALS_SCOOP_CLOSED.has(src);
        return (
          <motion.div
            key={src}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: i * 0.06 }}
            className="relative overflow-hidden rounded-xl border border-gray-100 shadow-sm bg-white"
            data-hs-id={`deals-scoop-image-${i + 1}`}
          >
            <img
              src={src}
              alt={`Dallas Desi deal promotion ${i + 1}`}
              loading="lazy"
              className={`w-full aspect-[3/4] object-cover hover:scale-[1.03] transition-transform duration-300${
                isClosed ? " opacity-80" : ""
              }`}
            />
            {isClosed && (
              <span
                className="absolute top-2 left-2 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wide text-white shadow-md bg-red-600"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                Closed
              </span>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

function DealsScoopTab({ query = "" }: { query?: string }) {
  const deals = FEATURED_BUSINESS_DEALS.filter((d) => matchesQuery(query, d.businessName, d.dealTitle, d.description, d.category));
  if (deals.length === 0) return <EmptyResults label="deals" />;
  return (
    <div>
      <DealsScoopImageStrip />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {deals.map((deal, i) => (
          <FeaturedDealCard key={deal._id} deal={deal} index={i} imageClassName="w-full h-[160px] object-cover" />
        ))}
      </div>
    </div>
  );
}

function NewOpeningsTab({ query = "" }: { query?: string }) {
  const featuredCaterer = useFeaturedCaterer();
  const showFeaturedCaterer =
    !!featuredCaterer &&
    matchesQuery(query, featuredCaterer.name, featuredCaterer.address, featuredCaterer.location);
  const featuredHaldirams = useFeaturedHaldirams();
  const showFeaturedHaldirams =
    !!featuredHaldirams &&
    matchesQuery(query, featuredHaldirams.name, featuredHaldirams.address, featuredHaldirams.city);
  const openings = NEW_OPENING_RESTAURANTS.filter((r) =>
    matchesQuery(query, r.name, r.address, r.city, ...(r.cuisines || []))
  );
  return (
    <div>
      <NewOpeningsIntro />
      {openings.length === 0 && !showFeaturedCaterer && !showFeaturedHaldirams ? (
        <EmptyResults label="new openings" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {openings.map((r, i) => (
            <RestaurantCard key={r.id} restaurant={r} index={i} />
          ))}
          {showFeaturedCaterer && featuredCaterer && (
            <CatererCard
              caterer={featuredCaterer}
              index={openings.length}
              isFeatured
            />
          )}
          {showFeaturedHaldirams && featuredHaldirams && (
            <RestaurantCard
              key={featuredHaldirams.id || "featured-haldirams"}
              restaurant={featuredHaldirams}
              index={openings.length + (showFeaturedCaterer ? 1 : 0)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// No celebrity content has been added yet — showing a clear "coming soon"
// empty state rather than fabricating names, photos, or stories.
function CelebrityScoopTab({ query = "" }: { query?: string }) {
  if (query.trim()) return <EmptyResults label="celebrity news" />;
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-4">
      <Flame className="w-8 h-8 text-orange-500 mb-3" />
      <h3
        className="text-lg font-bold"
        style={{ fontFamily: "'Poppins', sans-serif", color: "#082f6d" }}
      >
        Celebrity Scoop — Coming Soon
      </h3>
      <p
        className="mt-2 text-sm text-gray-400 max-w-sm"
        style={{ fontFamily: "'Montserrat', sans-serif" }}
      >
        Desi celebrity sightings, appearances, and news from around Dallas are on the way. Check back soon.
      </p>
    </div>
  );
}
